import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, Check } from "lucide-react";
import { useLang, UI } from "@/i18n/lang";
import { toast } from "@/components/ui/sonner";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = ["Website", "App", "Digital Product", "Book / Publishing", "Other"];

const field =
  "w-full bg-transparent border-b border-border py-3 text-base text-ink placeholder:text-stone/40 focus:border-bronze outline-none transition-colors duration-300";

export default function ReviewForm({ open, onClose, onSubmitted }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", service: "Website", quote: "", website: "" });
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setForm({ name: "", company: "", email: "", service: "Website", quote: "", website: "" });
    setRating(5);
    setHover(0);
    setDone(false);
  };

  const close = () => {
    onClose();
    setTimeout(reset, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/testimonials`, { ...form, rating });
      setDone(true);
      onSubmitted?.();
    } catch (err) {
      toast.error(t(UI.testimonials.error));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="review-modal"
          className="fixed inset-0 z-[120] flex items-center justify-center px-5 py-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={close}
            data-testid="review-modal-overlay"
          />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.33, 1, 0.68, 1] }}
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-paper p-8 md:p-10"
          >
            <button
              data-testid="review-close"
              onClick={close}
              aria-label={t(UI.testimonials.cancel)}
              className="absolute top-5 right-5 p-2 rounded-full text-stone hover:text-bronze transition-colors"
            >
              <X size={18} />
            </button>

            {done ? (
              <div data-testid="review-success" className="py-8 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-bronze/15 text-bronze">
                  <Check size={26} />
                </span>
                <h3 className="mt-6 font-serif text-3xl text-ink">{t(UI.testimonials.successTitle)}</h3>
                <p className="mt-3 text-sm text-stone leading-relaxed">{t(UI.testimonials.successBody)}</p>
                <button
                  data-testid="review-success-close"
                  onClick={close}
                  className="mt-8 rounded-full bg-bronze text-paper px-8 py-3 text-sm font-semibold tracking-wide hover:bg-[#f0be6e] transition-colors"
                >
                  {t(UI.testimonials.close)}
                </button>
              </div>
            ) : (
              <>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">
                  {t(UI.testimonials.eyebrow)}
                </p>
                <h3 className="mt-3 font-serif text-3xl md:text-4xl text-ink">{t(UI.testimonials.formTitle)}</h3>
                <p className="mt-3 text-sm text-stone leading-relaxed">{t(UI.testimonials.formIntro)}</p>

                <form onSubmit={submit} data-testid="review-form" className="mt-7 space-y-6">
                  {/* honeypot — hidden from humans, bots tend to fill it */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    className="absolute left-[-9999px] h-0 w-0 opacity-0"
                  />
                  <div>
                    <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">
                      {t(UI.testimonials.fRating)}
                    </label>
                    <div className="mt-2 flex gap-1.5" data-testid="review-rating">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          data-testid={`review-star-${n}`}
                          onClick={() => setRating(n)}
                          onMouseEnter={() => setHover(n)}
                          onMouseLeave={() => setHover(0)}
                          aria-label={`${n} / 5`}
                          className="p-0.5 transition-transform hover:scale-110"
                        >
                          <Star
                            size={26}
                            className={
                              (hover || rating) >= n ? "text-bronze" : "text-border"
                            }
                            fill={(hover || rating) >= n ? "currentColor" : "none"}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">
                        {t(UI.testimonials.fName)}
                      </label>
                      <input
                        data-testid="review-name"
                        required
                        maxLength={80}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className={field}
                        placeholder="Max Mustermann"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">
                        {t(UI.testimonials.fCompany)}
                      </label>
                      <input
                        data-testid="review-company"
                        maxLength={120}
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        className={field}
                        placeholder="—"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">
                      {t(UI.testimonials.fEmail)}
                    </label>
                    <input
                      data-testid="review-email"
                      type="email"
                      maxLength={200}
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={field}
                      placeholder="name@email.com"
                    />
                    <p className="mt-1.5 text-[11px] leading-snug text-stone/70">{t(UI.testimonials.emailNote)}</p>
                  </div>

                  <div>
                    <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">
                      {t(UI.testimonials.fService)}
                    </label>
                    <Select
                      value={form.service}
                      onValueChange={(v) => setForm({ ...form, service: v })}
                    >
                      <SelectTrigger
                        data-testid="review-service"
                        className="mt-1 w-full rounded-none border-0 border-b border-border bg-transparent px-0 py-3 h-auto text-base text-ink focus:border-bronze focus:ring-0 focus:ring-offset-0"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[200] border-border bg-paper text-ink">
                        {SERVICES.map((s) => (
                          <SelectItem
                            key={s}
                            value={s}
                            className="text-ink focus:bg-bronze/10 focus:text-ink cursor-pointer"
                          >
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">
                      {t(UI.testimonials.fQuote)}
                    </label>
                    <textarea
                      data-testid="review-quote"
                      required
                      minLength={10}
                      maxLength={600}
                      rows={4}
                      value={form.quote}
                      onChange={(e) => setForm({ ...form, quote: e.target.value })}
                      className={`${field} resize-none`}
                      placeholder="…"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="review-submit"
                    disabled={sending}
                    className="w-full rounded-full bg-bronze text-paper py-4 text-sm font-semibold tracking-wide hover:bg-[#f0be6e] transition-colors disabled:opacity-60"
                  >
                    {sending ? t(UI.testimonials.sending) : t(UI.testimonials.submit)}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
